import type {
  CacheEntry,
  DatabaseAdapter,
  ManifestState,
  PlayerEventRecord,
  ProofOfPlayRecord,
  StoredManifest,
} from '@signage/player-core';
import type { Database as SqlJsDatabase, SqlJsStatic } from 'sql.js';

/**
 * Where the database file lives: Android's app storage, or the browser's IndexedDB for the web
 * player. Writes must be atomic, so a power cut never leaves half a database behind.
 */
export interface ByteStorage {
  readFileBytes(path: string): Promise<Uint8Array>;
  writeFileAtomic(path: string, bytes: Uint8Array): Promise<void>;
}

/**
 * Local SQLite store for the Android and web players, backed by sql.js (SQLite compiled to
 * WebAssembly).
 *
 * WebAssembly is what makes this possible at all. `DatabaseAdapter` is a synchronous interface,
 * because the sync engine and cache manager read and write inside transactions where an `await`
 * would open a window for a half-applied update. Every native SQLite binding for a WebView is
 * asynchronous, so using one would have meant making the reliability-critical core async on
 * every platform. sql.js runs in-process and answers synchronously, so `player-core` is reused
 * here unchanged, including its test suite.
 *
 * The cost is that the database lives in memory and has to be written out deliberately. That is
 * what the persistence section below is for, and it is the part to be careful with: a signage
 * box can lose power at any moment, and a torn write here means a screen that boots with
 * nothing to play.
 */
export class WasmSqliteDatabase implements DatabaseAdapter {
  private readonly db: SqlJsDatabase;
  private readonly storage: ByteStorage;
  private readonly path: string;

  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private dirty = false;
  /** Depth of nested `transaction()` calls; the database is persisted when the outermost commits. */
  private txDepth = 0;
  private flushing: Promise<void> | null = null;
  private flushAgain = false;

  private constructor(
    sql: SqlJsStatic,
    bytes: Uint8Array | null,
    storage: ByteStorage,
    path: string,
  ) {
    this.db = bytes && bytes.byteLength > 0 ? new sql.Database(bytes) : new sql.Database();
    this.storage = storage;
    this.path = path;
    this.db.run('PRAGMA foreign_keys = ON');
    this.migrate();
  }

  /**
   * Opens the store, rebuilding it rather than throwing when the file on disk is unreadable.
   *
   * A lost database is recoverable: the device re-pairs or re-syncs and everything comes back
   * from the server. Refusing to start is not recoverable, because it leaves a dark screen that
   * only a site visit can fix.
   */
  static async open(
    sql: SqlJsStatic,
    storage: ByteStorage,
    path: string,
    onRecover?: (err: Error) => void,
  ): Promise<WasmSqliteDatabase> {
    let bytes: Uint8Array | null = null;
    try {
      bytes = await storage.readFileBytes(path);
    } catch {
      bytes = null; // First run on this device.
    }

    try {
      return new WasmSqliteDatabase(sql, bytes, storage, path);
    } catch (err) {
      onRecover?.(err as Error);
      const fresh = new WasmSqliteDatabase(sql, null, storage, path);
      fresh.dirty = true;
      await fresh.flush();
      return fresh;
    }
  }

  private migrate(): void {
    this.db.run(`
      CREATE TABLE IF NOT EXISTS settings (
        key   TEXT PRIMARY KEY,
        value TEXT NOT NULL
      );

      CREATE TABLE IF NOT EXISTS manifests (
        content_version TEXT PRIMARY KEY,
        state           TEXT NOT NULL,
        document        TEXT NOT NULL,
        saved_at        INTEGER NOT NULL,
        activated_at    INTEGER
      );
      CREATE INDEX IF NOT EXISTS manifests_state_idx ON manifests(state);

      CREATE TABLE IF NOT EXISTS media_cache (
        sha256        TEXT PRIMARY KEY,
        media_id      TEXT NOT NULL,
        path          TEXT NOT NULL,
        size_bytes    INTEGER NOT NULL,
        mime_type     TEXT NOT NULL,
        downloaded_at INTEGER NOT NULL,
        last_used_at  INTEGER NOT NULL,
        verified      INTEGER NOT NULL DEFAULT 0
      );
      CREATE INDEX IF NOT EXISTS media_cache_last_used_idx ON media_cache(last_used_at);

      CREATE TABLE IF NOT EXISTS proof_of_play (
        event_id     TEXT PRIMARY KEY,
        media_id     TEXT,
        playlist_id  TEXT,
        slideshow_id TEXT,
        schedule_id  TEXT,
        started_at   TEXT NOT NULL,
        completed_at TEXT NOT NULL,
        duration_ms  INTEGER NOT NULL,
        completed    INTEGER NOT NULL DEFAULT 1,
        created_at   INTEGER NOT NULL
      );
      CREATE INDEX IF NOT EXISTS proof_of_play_created_idx ON proof_of_play(created_at);

      CREATE TABLE IF NOT EXISTS player_events (
        event_id    TEXT PRIMARY KEY,
        level       TEXT NOT NULL,
        code        TEXT NOT NULL,
        message     TEXT NOT NULL,
        context     TEXT,
        occurred_at TEXT NOT NULL,
        created_at  INTEGER NOT NULL
      );
      CREATE INDEX IF NOT EXISTS player_events_created_idx ON player_events(created_at);

      CREATE UNIQUE INDEX IF NOT EXISTS manifests_one_per_state
        ON manifests(state) WHERE state IN ('active', 'pending');
    `);
  }

  /* --------------------------------------------------------------- persistence */

  /**
   * Marks the database as needing a write to disk.
   *
   * `urgent` starts the write immediately instead of waiting out the debounce. It is used for
   * the facts whose loss would strand the device or discard content already downloaded:
   * credentials, manifest changes and cache-index rows. Everything else — proof-of-play rows,
   * buffered events, last-used timestamps — rides the debounce, because losing a few seconds of
   * those to a power cut costs nothing.
   */
  private markDirty(urgent = false): void {
    this.dirty = true;
    if (this.txDepth > 0) return; // The outermost transaction persists once, on commit.
    if (urgent) {
      void this.flush();
      return;
    }
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, 400);
  }

  /**
   * Serializes the database and replaces the file on disk atomically.
   *
   * Concurrent calls are collapsed rather than interleaved: if a write is already running, one
   * more pass is queued after it, so what lands on disk is always a whole snapshot.
   */
  async flush(): Promise<void> {
    if (this.flushing) {
      this.flushAgain = true;
      return this.flushing;
    }
    if (!this.dirty) return;

    this.flushing = (async () => {
      try {
        do {
          this.flushAgain = false;
          this.dirty = false;
          const bytes = this.db.export();
          // Written under a temporary name and renamed into place, so a power cut mid-write
          // leaves the previous good database rather than a truncated one.
          await this.storage.writeFileAtomic(this.path, bytes);
        } while (this.flushAgain);
      } catch (err) {
        this.dirty = true; // Try again on the next change rather than losing the write silently.
        throw err;
      } finally {
        this.flushing = null;
      }
    })();

    return this.flushing;
  }

  async close(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    await this.flush();
    this.db.close();
  }

  /* ------------------------------------------------------------------- helpers */

  private all(sql: string, params: unknown[] = []): Record<string, unknown>[] {
    const stmt = this.db.prepare(sql);
    try {
      stmt.bind(params as never);
      const rows: Record<string, unknown>[] = [];
      while (stmt.step()) rows.push(stmt.getAsObject() as Record<string, unknown>);
      return rows;
    } finally {
      stmt.free();
    }
  }

  private one(sql: string, params: unknown[] = []): Record<string, unknown> | null {
    return this.all(sql, params)[0] ?? null;
  }

  private run(sql: string, params: unknown[] = []): void {
    this.db.run(sql, params as never);
  }

  /* ------------------------------------------------------------------ settings */

  getSetting(key: string): string | null {
    const row = this.one('SELECT value FROM settings WHERE key = ?', [key]);
    return row ? (row.value as string) : null;
  }

  setSetting(key: string, value: string): void {
    this.run(
      'INSERT INTO settings (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value',
      [key, value],
    );
    this.markDirty(true);
  }

  deleteSetting(key: string): void {
    this.run('DELETE FROM settings WHERE key = ?', [key]);
    this.markDirty(true);
  }

  /* ----------------------------------------------------------------- manifests */

  saveManifest(record: StoredManifest): void {
    this.run(
      `INSERT INTO manifests (content_version, state, document, saved_at, activated_at)
       VALUES (?, ?, ?, ?, ?)
       ON CONFLICT(content_version) DO UPDATE SET
         state = excluded.state,
         document = excluded.document,
         saved_at = excluded.saved_at,
         activated_at = excluded.activated_at`,
      [record.contentVersion, record.state, record.document, record.savedAt, record.activatedAt],
    );
    this.markDirty(true);
  }

  getManifest(state: ManifestState): StoredManifest | null {
    const row = this.one('SELECT * FROM manifests WHERE state = ? LIMIT 1', [state]);
    return row ? toManifest(row) : null;
  }

  /**
   * Promotes a manifest to active in one transaction, so the file on disk never shows two
   * active manifests or none.
   */
  activateManifest(contentVersion: string): void {
    this.transaction(() => {
      this.run("UPDATE manifests SET state = 'superseded' WHERE state = 'active'");
      this.run(
        "UPDATE manifests SET state = 'active', activated_at = ? WHERE content_version = ?",
        [Date.now(), contentVersion],
      );
    });
  }

  deleteManifest(contentVersion: string): void {
    this.run('DELETE FROM manifests WHERE content_version = ?', [contentVersion]);
    this.markDirty(true);
  }

  listManifests(): StoredManifest[] {
    return this.all('SELECT * FROM manifests ORDER BY saved_at DESC').map(toManifest);
  }

  /* --------------------------------------------------------------- media cache */

  upsertCacheEntry(entry: CacheEntry): void {
    this.run(
      `INSERT INTO media_cache
         (sha256, media_id, path, size_bytes, mime_type, downloaded_at, last_used_at, verified)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(sha256) DO UPDATE SET
         media_id = excluded.media_id,
         path = excluded.path,
         size_bytes = excluded.size_bytes,
         mime_type = excluded.mime_type,
         downloaded_at = excluded.downloaded_at,
         last_used_at = excluded.last_used_at,
         verified = excluded.verified`,
      [
        entry.sha256,
        entry.mediaId,
        entry.path,
        entry.sizeBytes,
        entry.mimeType,
        entry.downloadedAt,
        entry.lastUsedAt,
        entry.verified ? 1 : 0,
      ],
    );
    // Urgent: this row is what marks a file on disk as verified and usable. Losing it means
    // re-downloading media the device already holds.
    this.markDirty(true);
  }

  getCacheEntry(sha256: string): CacheEntry | null {
    const row = this.one('SELECT * FROM media_cache WHERE sha256 = ?', [sha256]);
    return row ? toCacheEntry(row) : null;
  }

  listCacheEntries(): CacheEntry[] {
    return this.all('SELECT * FROM media_cache ORDER BY last_used_at ASC').map(toCacheEntry);
  }

  deleteCacheEntry(sha256: string): void {
    this.run('DELETE FROM media_cache WHERE sha256 = ?', [sha256]);
    this.markDirty(true);
  }

  touchCacheEntry(sha256: string, lastUsedAt: number): void {
    this.run('UPDATE media_cache SET last_used_at = ? WHERE sha256 = ?', [lastUsedAt, sha256]);
    this.markDirty(); // Only affects eviction order; not worth a write of its own.
  }

  /* ------------------------------------------------------------- proof of play */

  enqueueProofOfPlay(entry: ProofOfPlayRecord): void {
    this.run(
      `INSERT OR REPLACE INTO proof_of_play
         (event_id, media_id, playlist_id, slideshow_id, schedule_id,
          started_at, completed_at, duration_ms, completed, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        entry.eventId,
        entry.mediaId,
        entry.playlistId,
        entry.slideshowId,
        entry.scheduleId,
        entry.startedAt,
        entry.completedAt,
        entry.durationMs,
        entry.completed ? 1 : 0,
        Date.now(),
      ],
    );
    this.markDirty();
  }

  takeProofOfPlay(limit: number): ProofOfPlayRecord[] {
    return this.all('SELECT * FROM proof_of_play ORDER BY created_at ASC LIMIT ?', [limit]).map(
      (row) => ({
        eventId: row.event_id as string,
        mediaId: (row.media_id as string | null) ?? null,
        playlistId: (row.playlist_id as string | null) ?? null,
        slideshowId: (row.slideshow_id as string | null) ?? null,
        scheduleId: (row.schedule_id as string | null) ?? null,
        startedAt: row.started_at as string,
        completedAt: row.completed_at as string,
        durationMs: row.duration_ms as number,
        completed: Boolean(row.completed),
      }),
    );
  }

  deleteProofOfPlay(eventIds: string[]): void {
    if (eventIds.length === 0) return;
    const placeholders = eventIds.map(() => '?').join(',');
    this.run(`DELETE FROM proof_of_play WHERE event_id IN (${placeholders})`, eventIds);
    this.markDirty();
  }

  countProofOfPlay(): number {
    const row = this.one('SELECT COUNT(*) AS n FROM proof_of_play');
    return row ? Number(row.n) : 0;
  }

  /* -------------------------------------------------------------------- events */

  enqueueEvent(entry: PlayerEventRecord): void {
    this.run(
      `INSERT OR REPLACE INTO player_events
         (event_id, level, code, message, context, occurred_at, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [
        entry.eventId,
        entry.level,
        entry.code,
        entry.message,
        entry.context,
        entry.occurredAt,
        Date.now(),
      ],
    );
    this.markDirty();
  }

  takeEvents(limit: number): PlayerEventRecord[] {
    return this.all('SELECT * FROM player_events ORDER BY created_at ASC LIMIT ?', [limit]).map(
      (row) => ({
        eventId: row.event_id as string,
        level: row.level as PlayerEventRecord['level'],
        code: row.code as string,
        message: row.message as string,
        context: (row.context as string | null) ?? null,
        occurredAt: row.occurred_at as string,
      }),
    );
  }

  deleteEvents(eventIds: string[]): void {
    if (eventIds.length === 0) return;
    const placeholders = eventIds.map(() => '?').join(',');
    this.run(`DELETE FROM player_events WHERE event_id IN (${placeholders})`, eventIds);
    this.markDirty();
  }

  /* --------------------------------------------------------------- transaction */

  transaction<T>(fn: () => T): T {
    // sql.js has no nested transactions; an inner call joins the outer one.
    if (this.txDepth > 0) return fn();

    this.db.run('BEGIN');
    this.txDepth += 1;
    try {
      const result = fn();
      this.db.run('COMMIT');
      return result;
    } catch (err) {
      this.db.run('ROLLBACK');
      this.dirty = false; // Nothing was committed, so there is nothing to persist.
      throw err;
    } finally {
      this.txDepth -= 1;
      // Anything the body marked dirty was held back until the commit; write it now.
      if (this.dirty) this.markDirty(true);
    }
  }
}

function toManifest(row: Record<string, unknown>): StoredManifest {
  return {
    contentVersion: row.content_version as string,
    state: row.state as ManifestState,
    document: row.document as string,
    savedAt: row.saved_at as number,
    activatedAt: (row.activated_at as number | null) ?? null,
  };
}

function toCacheEntry(row: Record<string, unknown>): CacheEntry {
  return {
    sha256: row.sha256 as string,
    mediaId: row.media_id as string,
    path: row.path as string,
    sizeBytes: row.size_bytes as number,
    mimeType: row.mime_type as string,
    downloadedAt: row.downloaded_at as number,
    lastUsedAt: row.last_used_at as number,
    verified: Boolean(row.verified),
  };
}
